import { useCallback, useEffect, useState } from 'react';
import type { McpServer } from '@orqforge/shared';
import { apiClient, type McpToolInfo } from '../lib/api-client';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/Tabs';

function errorMessage(err: unknown): string {
  if (!(err instanceof Error)) return 'Request failed';
  try {
    const parsed = JSON.parse(err.message) as { message?: string | string[] };
    if (typeof parsed.message === 'string') return parsed.message;
    if (Array.isArray(parsed.message)) return parsed.message.join(', ');
  } catch {
    return err.message;
  }
  return err.message;
}

export function McpPage() {
  const [servers, setServers] = useState<McpServer[]>([]);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [tools, setTools] = useState<McpToolInfo[]>([]);
  const [loadingTools, setLoadingTools] = useState(false);
  const [name, setName] = useState('');
  const [transport, setTransport] = useState<'http' | 'sse'>('http');
  const [url, setUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setServers(await apiClient.listMcpServers());
  }, []);

  useEffect(() => {
    void load().catch((err: unknown) => setError(errorMessage(err)));
  }, [load]);

  async function create(): Promise<void> {
    if (saving) return;
    setSaving(true);
    setError(null);
    try {
      await apiClient.createMcpServer({
        name: name.trim(),
        transport,
        url: url.trim() || undefined,
        enabled: true,
      });
      setShowCreate(false);
      setName('');
      setUrl('');
      await load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function toggleTools(server: McpServer): Promise<void> {
    if (expanded === server.id) {
      setExpanded(null);
      return;
    }
    setExpanded(server.id);
    setTools([]);
    setLoadingTools(true);
    try {
      setTools(await apiClient.listMcpTools(server.id));
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoadingTools(false);
    }
  }

  async function remove(server: McpServer): Promise<void> {
    setError(null);
    try {
      await apiClient.deleteMcpServer(server.id);
      if (expanded === server.id) setExpanded(null);
      await load();
    } catch (err) {
      setError(errorMessage(err));
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">MCP Servers</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Register Model Context Protocol servers to extend the platform with new tools. No core
            changes required. Inspect discovered tools per server.
          </p>
        </div>
        <Button size="sm" onClick={() => setShowCreate((open) => !open)}>Register server</Button>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p> : null}

      {showCreate ? (
        <Card className="mt-4 p-4">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">Register MCP server</h2>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <label>
              <span className="text-xs text-gray-500">Name</span>
              <Input className="mt-1" value={name} placeholder="Internal CRM" onChange={(e) => setName(e.target.value)} />
            </label>
            <div>
              <span className="mb-1.5 block text-xs text-gray-500">Transport</span>
              <Tabs value={transport} onValueChange={(value) => setTransport(value as 'http' | 'sse')}>
                <TabsList>
                  <TabsTrigger value="http">http</TabsTrigger>
                  <TabsTrigger value="sse">sse</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            <label className="sm:col-span-2">
              <span className="text-xs text-gray-500">URL</span>
              <Input
                className="mt-1"
                value={url}
                placeholder="https://mcp.example.com/sse"
                onChange={(e) => setUrl(e.target.value)}
              />
            </label>
          </div>
          <div className="mt-4 flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button size="sm" disabled={!name.trim() || saving} onClick={() => void create()}>
              {saving ? 'Registering…' : 'Register'}
            </Button>
          </div>
        </Card>
      ) : null}

      <div className="mt-4 grid gap-3 lg:grid-cols-2">
        {servers.length === 0 ? <p className="text-sm text-gray-500">No MCP servers registered yet.</p> : null}
        {servers.map((server) => (
          <Card key={server.id} className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <strong className="text-gray-900 dark:text-white">{server.name}</strong>
                <div className="text-xs text-gray-500">
                  {server.transport} · {server.transport === 'stdio' ? server.command : server.url}
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Badge variant={server.enabled ? 'success' : 'secondary'}>{server.enabled ? 'enabled' : 'disabled'}</Badge>
                <Button size="sm" variant="ghost" onClick={() => void toggleTools(server)}>Tools</Button>
                <Button size="sm" variant="ghost" className="text-red-600" aria-label="Delete MCP server" onClick={() => void remove(server)}>
                  Delete
                </Button>
              </div>
            </div>
            {expanded === server.id ? (
              <div className="mt-2 rounded-lg bg-gray-50 p-3 dark:bg-gray-950">
                {loadingTools ? <span className="text-sm text-gray-500">Loading tools</span> : null}
                {!loadingTools && tools.length === 0 ? <span className="text-sm text-gray-500">No tools discovered.</span> : null}
                {!loadingTools
                  ? tools.map((tool) => (
                    <div key={tool.name} className="border-b border-gray-200 py-1 last:border-0 dark:border-gray-800">
                      <code className="text-xs">{tool.name}</code>
                      {tool.description ? <span className="ml-2 text-xs text-gray-500">{tool.description}</span> : null}
                    </div>
                  ))
                  : null}
              </div>
            ) : null}
          </Card>
        ))}
      </div>
    </div>
  );
}
